import './styles.css'
import React, {useState, useCallback, useMemo} from 'react'
import {State} from 'country-state-city'
import countryList from 'react-select-country-list'
import {SingleValue} from 'react-select'
import {getCountries} from 'react-phone-number-input'
import type {CountryCode} from 'libphonenumber-js'

import CopyEmailBodySection from './subFeatures/CopyEmailBodySection'
import ErrorIcon from '../../../images/svg/ErrorIcon'
import FormGroup from './subFeatures/FormGroup'
import KegCollectionFormControl from './subFeatures/KegCollectionFormControl'
import RegionFormControl from './subFeatures/RegionFormControl'
import PhoneInputComponent from '../../../components/PhoneInput/PhoneInput'
import XIcon from '../../../images/svg/XIcon'
import logo from '../../../images/logos/kegstar-logo.svg'
import logoWhite from '../../../images/logos/kegstar-logo-white.svg'

import {
  getRegion,
  getCollectionDayKey,
  contactDetailsFormFields,
  regionFormFields,
  venueDetailsFormFields,
  filterCountryOptions,
  cantFindLocationEmailAddress,
  cantFindLocationMailToBody,
  cantFindLocationEmailSubjectText,
  getCountryData,
} from './utils/utils'
import {VenueSubmissionFormState, daysOfWeek, KegLocation} from '../../../types'
import {
  sanitizedInput,
  kegQuantityInputRegex,
  getIsNumberOrStringRepresentationOfNumber,
} from '../../../utils'
import useErrorFormState from './utils/useErrorFormState'
import useSessionStorageState from '../../../hooks/useSessionStorageState'
import {useTheme} from '../../../contexts/ThemeContext'
import {useToast} from '../../../contexts/ToastContext'

type AddressKey = keyof VenueSubmissionFormState['address']
type ContactKey = keyof VenueSubmissionFormState['contact']
type DayOfWeek = keyof typeof daysOfWeek

const ADDRESS_KEYS: AddressKey[] = [
  'addressLine1',
  'addressLine2',
  'city',
  'postalCode',
  'stateOrProvince',
  'country',
]

const CONTACT_KEYS: ContactKey[] = [
  'contactName',
  'contactEmail',
  'contactPhone',
  'phoneCountryCode',
]

function isAddressKey(key: string): key is AddressKey {
  return (ADDRESS_KEYS as string[]).includes(key)
}

function isContactKey(key: string): key is ContactKey {
  return (CONTACT_KEYS as string[]).includes(key)
}

interface VenueSubmissionFormProps {
  setToggleVenueSubmissionForm: React.Dispatch<React.SetStateAction<boolean>>
  venueSubmissionStatus: 'idle' | 'submitting' | 'submitted' | 'error'
  setVenueSubmissionStatus: React.Dispatch<
    React.SetStateAction<'idle' | 'submitting' | 'submitted' | 'error'>
  >
}

const initialFormValues: VenueSubmissionFormState = {
  region: 'UKE',
  venueName: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    postalCode: '',
    stateOrProvince: {value: '', label: ''},
    country: {value: '', label: ''},
  },
  contact: {
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    phoneCountryCode: undefined,
  },
  additionalNotes: '',
  includeKegCollection: false,
  collectionRequest: {
    venueOpenMonday: false,
    venueOpenTuesday: false,
    venueOpenWednesday: false,
    venueOpenThursday: false,
    venueOpenFriday: false,
    startHour: 12,
    endHour: 18,
    kegLocation: 'Inside',
    '20L': '',
    '30L': '',
    '50L': '',
    '9G': '',
    pickupInstructions: '',
  },
}

export default function VenueSubmissionForm({
  setToggleVenueSubmissionForm,
}: VenueSubmissionFormProps) {
  const countryOptions = useMemo(() => countryList().getData() || [], [])
  // Filter country options to only those supported by react-phone-number-input
  const filteredCountryOptions = useMemo(
    () => filterCountryOptions(countryOptions, getCountries()),
    [countryOptions]
  )
  const {showToast, hideToast} = useToast()
  const {effectiveTheme} = useTheme()
  const logoImage = effectiveTheme === 'light' ? logo : logoWhite
  // Form state management, session storage hook
  const [formValues, setFormValues, resetFormValues] =
    useSessionStorageState<VenueSubmissionFormState>({
      key: 'KegstarCollectionsApp.VenueSubmissionForm',
      initialValue: initialFormValues,
    })

  // Local component state
  const [flagFormErrors, setFlagFormErrors] = useState<boolean>(false)

  const stateOptions = useMemo(() => {
    if (!formValues.address.country?.value) return []
    const states = State.getStatesOfCountry(formValues.address.country.value)
    return states.length > 0
      ? states.map((state) => ({
          value: state.isoCode,
          label: state.name,
        }))
      : []
  }, [formValues.address.country])

  const isStateRequired = useMemo(() => {
    return stateOptions.length > 0
  }, [stateOptions])

  // Error state management
  const {errorFormState} = useErrorFormState(formValues, isStateRequired)

  const regionFormFieldsWithErrors = useMemo((): {
    label: string
    placeholder: string
    required: boolean
    id: string
    errorMessage?: string | null
    state: {
      label: string
      placeholder: string
      required: boolean
      id: string
      errorMessage?: string | null
    }
  } => {
    return {
      ...regionFormFields,
      errorMessage: (errorFormState as Record<string, string>)['country'] || null,
      state: {
        ...regionFormFields.state,
        errorMessage: (errorFormState as Record<string, string>)['stateOrProvince'] || null,
      },
    }
  }, [errorFormState])

  // Derive error summary whenever errorFormState or formValues change
  const errorSummary = React.useMemo(() => {
    const errors = Object.values(errorFormState).filter(Boolean) as string[]

    if (formValues.includeKegCollection) {
      const {collectionRequest} = formValues

      const noKegs =
        collectionRequest['20L'] === '' &&
        collectionRequest['30L'] === '' &&
        collectionRequest['50L'] === '' &&
        collectionRequest['9G'] === ''

      if (noKegs) {
        errors.push('At least one keg quantity must be greater than zero.')
      }

      const noDaysSelected =
        !collectionRequest.venueOpenMonday &&
        !collectionRequest.venueOpenTuesday &&
        !collectionRequest.venueOpenWednesday &&
        !collectionRequest.venueOpenThursday &&
        !collectionRequest.venueOpenFriday

      if (noDaysSelected) {
        errors.push('At least one collection day must be selected.')
      }
    }

    // Dedupe, just in case
    return Array.from(new Set(errors))
  }, [errorFormState, formValues])

  // Helper to get input value based on form key - used in FormGroup
  const getInputValue = useCallback(
    (formKey: string) => {
      if (isAddressKey(formKey)) {
        return formValues.address[formKey]
      }

      if (isContactKey(formKey)) {
        return formValues.contact[formKey]
      }

      return formValues[formKey as keyof VenueSubmissionFormState]
    },
    [formValues]
  )

  // Event handlers
  const handleFormFieldOnChange = useCallback(
    (formKey: string, value: string | boolean | number) => {
      if (formKey in formValues.address) {
        setFormValues((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            [formKey]: sanitizedInput(value as string),
          },
        }))
      } else if (formKey in formValues.contact) {
        setFormValues((prev) => ({
          ...prev,
          contact: {
            ...prev.contact,
            [formKey]: sanitizedInput(value as string),
          },
        }))
      } else if (formKey in formValues.collectionRequest) {
        setFormValues((prev) => ({
          ...prev,
          collectionRequest: {
            ...prev.collectionRequest,
            [formKey]: value,
          },
        }))
      } else {
        setFormValues((prev) => ({
          ...prev,
          [formKey]: sanitizedInput(value as string),
        }))
      }
    },
    [formValues.address, formValues.contact, formValues.collectionRequest, setFormValues]
  )

  const handleKegQuantityInputOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const {value, name} = event.target
      if (!getIsNumberOrStringRepresentationOfNumber(value)) return
      if (value.length > 3 || !kegQuantityInputRegex.test(value)) return
      handleFormFieldOnChange(name, value)
    },
    [handleFormFieldOnChange]
  )

  const handleAvailableDaysOnClick = useCallback(
    (day: DayOfWeek) => {
      setFormValues((prev) => ({
        ...prev,
        collectionRequest: {
          ...prev.collectionRequest,
          [getCollectionDayKey(day)]: !prev.collectionRequest[getCollectionDayKey(day)],
        },
      }))
    },
    [setFormValues]
  )

  const handleTimeRangeOnChange = useCallback(
    (value: number[]) => {
      handleFormFieldOnChange('startHour', value[0])
      handleFormFieldOnChange('endHour', value[1])
    },
    [handleFormFieldOnChange]
  )

  const handleKegLocationOnChange = useCallback(
    (location: KegLocation) => {
      handleFormFieldOnChange('kegLocation', location)
    },
    [handleFormFieldOnChange]
  )

  const handlePickupInstructionsOnChange = useCallback(
    (instructions: string) => {
      handleFormFieldOnChange('pickupInstructions', instructions)
    },
    [handleFormFieldOnChange]
  )

  const handleRegionFormControlOnChange = useCallback(
    (type: string, data: SingleValue<{label: string; value: string}>) => {
      if (!data) return
      if (type === 'country') {
        setFormValues((prev) => {
          if (prev.address.country?.value === data.value) {
            return prev
          }
          return {
            ...prev,
            region: getRegion(data.value),
            address: {
              ...prev.address,
              country: data,
              stateOrProvince: {label: '', value: ''},
            },
            contact: {
              ...prev.contact,
              phoneCountryCode: data.value as CountryCode | undefined,
              contactPhone: '',
            },
          }
        })
      }
      if (type === 'state') {
        setFormValues((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            stateOrProvince: data,
          },
        }))
      }
    },
    [setFormValues]
  )

  const handleIncludeKegCollectionDetailsOnChange = useCallback(
    (includeDetails: boolean) => {
      setFormValues((prev) => ({
        ...prev,
        includeKegCollection: includeDetails,
      }))
    },
    [setFormValues]
  )

  const handleOpenEmailToSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault()

    const hasErrors = errorSummary.length > 0
    setFlagFormErrors(hasErrors)
    if (hasErrors) return

    const countryCode = formValues.address.country?.value as CountryCode | undefined
    const to = cantFindLocationEmailAddress(countryCode ?? ('GB' as CountryCode))

    const subject = cantFindLocationEmailSubjectText
    const body = cantFindLocationMailToBody({
      venueName: formValues.venueName,
      address: formValues.address,
      contactName: formValues.contact.contactName,
      contactEmail: formValues.contact.contactEmail,
      contactPhone: formValues.contact.contactPhone,
      additionalNotes: formValues.additionalNotes,
      includeKegCollection: formValues.includeKegCollection,
      collectionRequest: formValues.collectionRequest,
    })

    const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
      body
    )}`

    // Fire and forget – we can’t know if it succeeded
    window.location.href = mailto

    const renderedToastChild = (
      <div className="flex flex-col items-center justify-center gap-1 px-4 pt-1 pb-5">
        <button
          className="w-full bg-green-500 hover:bg-green-400 dark:bg-green-600 dark:hover:bg-green-500 text-white py-2 rounded"
          onClick={() => {
            resetFormValues()
            setToggleVenueSubmissionForm(false)
            hideToast()
          }}
        >
          Email Has Been Sent
        </button>
        <div>
          <CopyEmailBodySection
            formValues={formValues}
            cantFindLocationEmailAddress={cantFindLocationEmailAddress}
            cantFindLocationMailToBody={cantFindLocationMailToBody}
            cantFindLocationEmailSubjectText={cantFindLocationEmailSubjectText}
          />
        </div>
      </div>
    )
    showToast({
      title: 'Email App Opened',
      description: `A draft email has been opened in your default email app. Please review and send it to complete your venue submission.
      Once you've sent the email, click 'Email Has Been Sent' to close this message.`,
      variant: 'success',
      autoDismiss: false,
      overlayMode: 'dim',
      children: renderedToastChild,
    })
  }

  const handlePhoneCountryChange = useCallback(
    (countryCode: string) => {
      // Delay to ensure phone input updates correctly
      setTimeout(() => {
        setFormValues((prev) => {
          if (prev.contact.phoneCountryCode === countryCode) {
            return prev
          }
          return {
            ...prev,
            region: getRegion(countryCode),
            address: {
              ...prev.address,
              country: getCountryData(countryCode, countryOptions),
              stateOrProvince: {value: '', label: ''},
            },
            contact: {
              ...prev.contact,
              phoneCountryCode: countryCode as CountryCode,
              contactPhone: '',
            },
          }
        })
      }, 100)
    },
    [setFormValues, countryOptions]
  )

  const handleResetForm = () => {
    resetFormValues()
    setFlagFormErrors(false)
  }

  // Render helpers
  const renderFormFields = (
    fields: typeof venueDetailsFormFields | typeof contactDetailsFormFields,
    options?: {autoFocusFirst?: boolean}
  ) =>
    fields.map((formField, index) => {
      const msg = flagFormErrors
        ? (errorFormState as Record<string, string>)[formField.formKey]
        : null
      const shouldAutoFocus = Boolean(options?.autoFocusFirst && index === 0)
      return (
        <FormGroup
          key={formField.formKey}
          formField={{...formField, errorMessage: msg}}
          handleFormFieldOnChange={handleFormFieldOnChange}
          getInputValue={getInputValue}
          autoFocus={shouldAutoFocus}
        />
      )
    })

  const renderedFormMainContent = (
    <main className="grow overflow-auto px-7 py-7 animate-into-view-1">
      <p className="text-sm text-slate-500 dark:text-slate-400 m-0">
        Fill out the form below to submit your venue. You can optionally submit a keg collection
        request at the same time.
      </p>

      <form className="mt-4">
        <h2 className="text-slate-500 dark:text-slate-300 text-lg font-semibold text-center mb-5">
          Venue Details
        </h2>
        {renderFormFields(venueDetailsFormFields.slice(0, 5), {autoFocusFirst: true})}

        <RegionFormControl
          countryOptions={filteredCountryOptions}
          stateOptions={stateOptions}
          handleRegionFormControlOnChange={handleRegionFormControlOnChange}
          formField={regionFormFieldsWithErrors}
          storedCountryData={formValues.address.country}
          storedStateData={formValues.address.stateOrProvince}
          flagFormErrors={flagFormErrors}
        />

        {renderFormFields(venueDetailsFormFields.slice(5))}

        <h2 className="text-slate-500 dark:text-slate-300 text-lg font-semibold text-center mb-5">
          Contact Details
        </h2>
        {renderFormFields(contactDetailsFormFields)}

        <PhoneInputComponent
          phoneInputValue={formValues.contact.contactPhone}
          onChangeCallback={(value) => handleFormFieldOnChange('contactPhone', value)}
          defaultRegion={formValues.contact.phoneCountryCode}
          isDisabled={false}
          onCountryChangeCallback={handlePhoneCountryChange}
          flagFormErrors={flagFormErrors}
        />

        <div className="flex items-center justify-center my-7">
          <label
            className="text-slate-500 dark:text-slate-300 text-base"
            htmlFor="includeCollectionDetails"
          >
            Include a keg collection request?
          </label>
          <input
            id="includeCollectionDetails"
            type="checkbox"
            className="custom-checkbox ml-2"
            checked={formValues.includeKegCollection}
            onChange={(e) => handleIncludeKegCollectionDetailsOnChange(e.target.checked)}
          />
        </div>

        {formValues.includeKegCollection && (
          <KegCollectionFormControl
            formValues={formValues}
            handleKegQuantityInputOnChange={handleKegQuantityInputOnChange}
            handleAvailableDaysOnClick={handleAvailableDaysOnClick}
            handleTimeRangeOnChange={handleTimeRangeOnChange}
            handleKegLocationOnChange={handleKegLocationOnChange}
            handlePickupInstructionsOnChange={handlePickupInstructionsOnChange}
            flagFormErrors={flagFormErrors}
            chosenLocation={{
              // Dummy ids to satisfy typescript type definition
              customerId: '00000000-0000-0000-0000-000000000000',
              addressId: '00000000-0000-0000-0000-000000000000',
              name: '',
              address1: '',
              city: '',
              stateOrProvince: '',
              country: formValues?.address?.country?.value || '',
              postalCode: '',
              latitude: 0,
              longitude: 0,
              distanceKm: 0,
              errorMessage: null,
              venueCollectionInfo: null,
            }}
          />
        )}

        {flagFormErrors && errorSummary.length > 0 && (
          <div className="bg-red-100 dark:bg-red-950/30 border border-red-400 dark:border-red-500/60 text-red-700 dark:text-red-200 px-4 py-3 rounded relative mb-5 text-sm">
            <span className="flex items-center">
              <ErrorIcon className="inline-block h-5 w-5 mr-2 align-middle" />
              <strong className="font-bold">Fix the following errors to proceed:</strong>
            </span>
            <ul className="list-disc list-inside mt-2">
              {errorSummary.map((error, index) => (
                <li key={`${index}-${error}`}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-3 justify-center mt-6">
          <button
            type="submit"
            className={[
              'w-full font-semibold py-2 px-4 rounded focus:outline-none text-white',
              'focus:bg-sky-400 dark:focus:bg-sky-500',
              'bg-sky-500 hover:bg-sky-400 focus:bg-sky-400 dark:bg-sky-600 dark:hover:bg-sky-500',
            ].join(' ')}
            onClick={handleOpenEmailToSubmit}
          >
            Open Email to Submit
          </button>
          <button
            type="button"
            className={[
              'w-full font-semibold py-2 px-4 rounded focus:outline-none text-white',
              'bg-red-500 hover:bg-red-400 focus:bg-red-400 dark:bg-red-600 dark:hover:bg-red-500',
            ].join(' ')}
            onClick={handleResetForm}
          >
            Reset Form
          </button>
        </div>
      </form>
    </main>
  )

  return (
    <div
      className="slide-left-animation h-full max-w-md mx-auto flex flex-col bg-white dark:bg-slate-900"
      onClick={(e) => e.stopPropagation()}
    >
      <header className="flex items-center shrink-0 p-4 shadow-sm border-b border-slate-200 dark:border-slate-700">
        <div className="flex flex-1">
          <img
            src={logoImage}
            alt="Kegstar Logo"
            className="w-24 h-auto"
          />
        </div>
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={() => setToggleVenueSubmissionForm(false)}
            aria-label="Close venue submission form"
            className={[
              'ml-2 inline-flex h-7 w-7 items-center justify-center rounded-full',
              'transition focus:outline-none focus:ring-2 focus:ring-offset-2',
              'focus:ring-slate-500 dark:focus:ring-slate-400 focus:ring-offset-white dark:focus:ring-offset-slate-900 hover:opacity-80 cursor-pointer',
            ].join(' ')}
          >
            <XIcon className={'h-5 w-5 text-slate-500 dark:text-slate-300'} />
          </button>
        </div>
      </header>
      {renderedFormMainContent}
    </div>
  )
}
